import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import swal from "sweetalert";
import {
  CHANNELS,
  Channel,
  createSend,
  CreateSendInput,
  OUTPUT_TYPES,
  OutputType,
  searchObjects,
  WA_CHANNELS,
} from "services/push-automation.service";
import { getProgramsList } from "services/programs.service";
import { searchSessions, searchWaUserChannels } from "services/content-manager.service";
import { searchBotChannels } from "data/bots-channels";
import AsyncMultiSelect, { Option } from "./AsyncMultiSelect";
import AsyncSingleSelect from "./AsyncSingleSelect";

type Props = {
  visible: boolean;
  close: () => void;
  refresh: () => void;
};

async function fetchProgramOptions(search: string): Promise<Option[]> {
  const { data } = await getProgramsList(1, search);
  return data.results.map((program: any) => ({ label: program.name, value: program.id }));
}

async function fetchUserChannelOptions(search: string): Promise<Option[]> {
  const { data } = await searchWaUserChannels(search, 1);
  return data.results
    .filter((userChannel: any) => userChannel.user_channel_id)
    .map((userChannel: any) => {
      const name = `${userChannel.first_name ?? ""} ${userChannel.last_name ?? ""}`.trim();
      return {
        label: name ? `${name} (${userChannel.user_channel_id})` : userChannel.user_channel_id,
        // id de fila (messenger_users_userchannel.id), no el telefono: el mismo telefono
        // puede tener mas de una cuenta, filtrar por telefono puede mandarle a las dos.
        value: userChannel.id,
      };
    });
}

async function fetchBotPhoneOptions(search: string): Promise<Option[]> {
  const { data } = await searchBotChannels(search, 1);
  return data.results
    .filter((bot: any) => bot.phone_number)
    .map((bot: any) => ({
      label: bot.bot_name ? `${bot.bot_name} (${bot.phone_number})` : bot.phone_number,
      // id, no phone_number: phone_number no es unico en bot_channel (sin constraint),
      // filtrar por telefono puede resolver a mas de un bot_channel y traer usuarios de
      // un bot viejo/duplicado con el mismo numero.
      value: bot.id,
    }));
}

async function fetchSessionOptions(search: string): Promise<Option[]> {
  const { data } = await searchSessions(search, 1);
  return data.results.map((session: any) => ({ label: `${session.name} (${session.id})`, value: session.id }));
}

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  bgcolor: "white",
  p: { xs: 2, sm: 4 },
  display: "flex",
  flexDirection: "column" as const,
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", md: "70vw" },
  maxHeight: "90vh",
  overflowY: "auto" as const,
};

function LocalizedTextFields({
  label,
  es,
  pt,
  en,
  onChange,
}: {
  label: string;
  es: string;
  pt: string;
  en: string;
  onChange: (lang: "es" | "pt" | "en", value: string) => void;
}) {
  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="subtitle2">{label}</Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField label="Español" value={es} onChange={(e) => onChange("es", e.target.value)} fullWidth required />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField label="Portugués" value={pt} onChange={(e) => onChange("pt", e.target.value)} fullWidth />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField label="Inglés" value={en} onChange={(e) => onChange("en", e.target.value)} fullWidth />
      </Grid>
    </Grid>
  );
}

function PushSendModal({ visible, close, refresh }: Props) {
  const [channel, setChannel] = useState<Channel>("WHATSAPP POR PROGRAMA");
  const [scheduledAt, setScheduledAt] = useState("");
  const [limit, setLimit] = useState("");
  const [template, setTemplate] = useState({ es: "", pt: "", en: "" });
  const [session, setSession] = useState<{ es: Option | null; pt: Option | null; en: Option | null }>({
    es: null,
    pt: null,
    en: null,
  });
  const [message, setMessage] = useState({ es: "", pt: "", en: "" });
  const [output, setOutput] = useState<OutputType>("Activity");
  const [mediaType, setMediaType] = useState<"TEXT" | "IMAGE" | "VIDEO">("TEXT");
  const [mediaLink, setMediaLink] = useState("");
  // object_id de un `objects` existente para este template+es, autoseleccionado al escribir
  // el nombre del template (ver /objects/search). Si esta seteado y no esta dañado (tiene
  // url), la URL se muestra de solo lectura — no se permite pisar la relacion ya cacheada.
  const [mediaObjectId, setMediaObjectId] = useState<string | null>(null);
  const [mediaDamaged, setMediaDamaged] = useState(false);
  const [mediaHasCachedId, setMediaHasCachedId] = useState(false);
  const [mediaCachedAccounts, setMediaCachedAccounts] = useState<{ account_id: number; uploaded_at: string }[]>([]);
  const [mediaLookupLoading, setMediaLookupLoading] = useState(false);
  const [programIds, setProgramIds] = useState<Option[]>([]);
  const [userChannels, setUserChannels] = useState<Option[]>([]);
  const [botPhones, setBotPhones] = useState<Option[]>([]);
  const [offset, setOffset] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [sendSuppressed, setSendSuppressed] = useState(false);
  const [suppressedDays, setSuppressedDays] = useState("5");
  const [inactiveRange, setInactiveRange] = useState(false);
  const [ageMin, setAgeMin] = useState("");
  const [ageMax, setAgeMax] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isWaChannel = WA_CHANNELS.includes(channel);

  // Autoselecciona el `objects` existente para este template en Español apenas se termina de
  // escribir el nombre (debounced) — misma relacion name=`${templateName}_es` que usa el
  // backend (persistWaTemplateLinks / resolveMediaId), asi el operador no vuelve a pegar una
  // URL para un template que ya tiene media_id cacheado.
  useEffect(() => {
    if (!isWaChannel || mediaType === "TEXT" || !template.es.trim()) {
      setMediaObjectId(null);
      setMediaDamaged(false);
      setMediaHasCachedId(false);
      setMediaCachedAccounts([]);
      return;
    }
    const expectedName = `${template.es.trim()}_es`;
    const timeoutId = setTimeout(async () => {
      setMediaLookupLoading(true);
      try {
        const { data } = await searchObjects(expectedName);
        const match = data.find((row) => row.name === expectedName);
        if (match) {
          setMediaObjectId(match.id);
          setMediaDamaged(!match.url);
          setMediaLink(match.url ?? "");
          setMediaHasCachedId(match.has_media_id);
          setMediaCachedAccounts(match.media_ids_by_account);
        } else {
          setMediaObjectId(null);
          setMediaDamaged(false);
          setMediaHasCachedId(false);
          setMediaCachedAccounts([]);
        }
      } catch {
        setMediaObjectId(null);
        setMediaDamaged(false);
        setMediaHasCachedId(false);
        setMediaCachedAccounts([]);
      } finally {
        setMediaLookupLoading(false);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.es, mediaType, isWaChannel]);

  const mediaLinkLocked = mediaObjectId !== null && !mediaDamaged;

  function reset() {
    setChannel("WHATSAPP POR PROGRAMA");
    setScheduledAt("");
    setLimit("");
    setTemplate({ es: "", pt: "", en: "" });
    setSession({ es: null, pt: null, en: null });
    setMessage({ es: "", pt: "", en: "" });
    setOutput("Activity");
    setMediaType("TEXT");
    setMediaLink("");
    setMediaObjectId(null);
    setMediaDamaged(false);
    setMediaHasCachedId(false);
    setMediaCachedAccounts([]);
    setProgramIds([]);
    setUserChannels([]);
    setBotPhones([]);
    setOffset("");
    setOrderBy("");
    setSendSuppressed(false);
    setSuppressedDays("5");
    setInactiveRange(false);
    setAgeMin("");
    setAgeMax("");
  }

  async function submit() {
    const payload: CreateSendInput = {
      channel,
      scheduled_at: scheduledAt,
    };
    if (limit) payload.limit = Number(limit);

    if (isWaChannel) {
      payload.template = { es: template.es, ...(template.pt ? { pt: template.pt } : {}), ...(template.en ? { en: template.en } : {}) };
      if (!session.es) {
        swal("Afinidata", "Selecciona la sesión en Español", "error");
        return;
      }
      payload.session = {
        es: String(session.es.value),
        ...(session.pt ? { pt: String(session.pt.value) } : {}),
        ...(session.en ? { en: String(session.en.value) } : {}),
      };
      if (mediaLink || mediaObjectId) {
        payload.media = {
          es: {
            type: mediaType,
            ...(mediaLink ? { link: mediaLink } : {}),
            ...(mediaObjectId ? { object_id: mediaObjectId } : {}),
          },
        };
      }
    } else {
      payload.message = { es: message.es, ...(message.pt ? { pt: message.pt } : {}), ...(message.en ? { en: message.en } : {}) };
      payload.output = output;
    }

    if (programIds.length) payload.program_ids = programIds.map((option) => Number(option.value));
    if (userChannels.length) payload.user_channel_row_ids = userChannels.map((option) => Number(option.value));
    if (botPhones.length) payload.bot_channel_ids = botPhones.map((option) => String(option.value));
    if (offset) payload.offset = Number(offset);
    if (orderBy) payload.order_by = orderBy;
    if (sendSuppressed) {
      payload.send_suppressed = true;
      if (suppressedDays) payload.suppressed_days = Number(suppressedDays);
    }
    if (inactiveRange) payload.inactive_range = true;
    if (ageMin && ageMax) {
      payload.age_min = Number(ageMin);
      payload.age_max = Number(ageMax);
    }

    setSubmitting(true);
    try {
      const result = await createSend(payload);
      const preview = result.data.preview;
      await swal(
        "Envío programado",
        preview.note ? preview.note : `Se enviará a ${preview.total} usuarios el ${scheduledAt}.`,
        "success"
      );
      reset();
      refresh();
      close();
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      if (apiError) swal("Afinidata", apiError, "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={visible}>
      <Box sx={modalStyle}>
        <Typography variant="h6" mb={1}>
          Configurar envío de push
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="channel-label">Canal</InputLabel>
              <Select
                labelId="channel-label"
                label="Canal"
                value={channel}
                onChange={(e) => setChannel(e.target.value as Channel)}
              >
                {CHANNELS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Fecha de envío"
              type="date"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Límite (vacío = sin límite)"
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Offset" type="number" value={offset} onChange={(e) => setOffset(e.target.value)} fullWidth />
          </Grid>

          {isWaChannel ? (
            <>
              <Grid item xs={12}>
                <LocalizedTextFields
                  label="Template"
                  es={template.es}
                  pt={template.pt}
                  en={template.en}
                  onChange={(lang, value) => setTemplate((prev) => ({ ...prev, [lang]: value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" mb={1}>
                  Sesión
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <AsyncSingleSelect
                      label="Español"
                      value={session.es}
                      onChange={(value) => setSession((prev) => ({ ...prev, es: value }))}
                      fetchOptions={fetchSessionOptions}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <AsyncSingleSelect
                      label="Portugués"
                      value={session.pt}
                      onChange={(value) => setSession((prev) => ({ ...prev, pt: value }))}
                      fetchOptions={fetchSessionOptions}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <AsyncSingleSelect
                      label="Inglés"
                      value={session.en}
                      onChange={(value) => setSession((prev) => ({ ...prev, en: value }))}
                      fetchOptions={fetchSessionOptions}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={mediaType === "TEXT" ? 12 : 6}>
                <FormControl fullWidth>
                  <InputLabel id="media-type-label">Tipo de media</InputLabel>
                  <Select
                    labelId="media-type-label"
                    label="Tipo de media"
                    value={mediaType}
                    onChange={(e) => {
                      const nextType = e.target.value as "TEXT" | "IMAGE" | "VIDEO";
                      setMediaType(nextType);
                      if (nextType === "TEXT") setMediaLink("");
                    }}
                  >
                    <MenuItem value="TEXT">TEXT (sin adjunto)</MenuItem>
                    <MenuItem value="IMAGE">IMAGE</MenuItem>
                    <MenuItem value="VIDEO">VIDEO</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {mediaType !== "TEXT" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Media URL"
                    value={mediaLink}
                    onChange={(e) => setMediaLink(e.target.value)}
                    fullWidth
                    required
                    disabled={mediaLinkLocked}
                    helperText={
                      mediaLookupLoading
                        ? "Buscando media ya cacheada para este template…"
                        : mediaLinkLocked
                        ? "Reusando media ya asociada a este template — no editable"
                        : mediaObjectId && mediaDamaged
                        ? "Este template ya tiene una relación pero sin URL cargada (dañado) — se reparará con esta URL"
                        : "Template nuevo: se creará la relación con esta URL"
                    }
                  />
                  {!mediaLookupLoading && mediaObjectId && (
                    <Typography variant="caption" display="block" mt={0.5} color={mediaHasCachedId ? "success.main" : "warning.main"}>
                      object_id: {mediaObjectId}
                      {mediaDamaged
                        ? " · sin URL cargada (dañado)"
                        : mediaHasCachedId
                        ? ` · media_id vigente en ${mediaCachedAccounts.length} cuenta(s) (subido ${new Date(mediaCachedAccounts[0].uploaded_at).toLocaleDateString()})`
                        : " · sin media_id subido a Meta todavía (se subirá en el próximo envío)"}
                    </Typography>
                  )}
                </Grid>
              )}
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <LocalizedTextFields
                  label="Mensaje"
                  es={message.es}
                  pt={message.pt}
                  en={message.en}
                  onChange={(lang, value) => setMessage((prev) => ({ ...prev, [lang]: value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="output-label">Output</InputLabel>
                  <Select labelId="output-label" label="Output" value={output} onChange={(e) => setOutput(e.target.value as OutputType)}>
                    {OUTPUT_TYPES.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <AsyncMultiSelect
              label="Programas"
              value={programIds}
              onChange={setProgramIds}
              fetchOptions={fetchProgramOptions}
              disabled={userChannels.length > 0}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AsyncMultiSelect
              label="Usuarios (user channels)"
              value={userChannels}
              onChange={setUserChannels}
              fetchOptions={fetchUserChannelOptions}
              disabled={programIds.length > 0}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AsyncMultiSelect label="Bots (teléfono)" value={botPhones} onChange={setBotPhones} fetchOptions={fetchBotPhoneOptions} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Order by" value={orderBy} onChange={(e) => setOrderBy(e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Edad mínima" type="number" value={ageMin} onChange={(e) => setAgeMin(e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Edad máxima" type="number" value={ageMax} onChange={(e) => setAgeMax(e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={<Checkbox checked={sendSuppressed} onChange={(e) => setSendSuppressed(e.target.checked)} />}
              label="Incluir suprimidos"
            />
          </Grid>
          {sendSuppressed && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Días de supresión"
                type="number"
                value={suppressedDays}
                onChange={(e) => setSuppressedDays(e.target.value)}
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={<Checkbox checked={inactiveRange} onChange={(e) => setInactiveRange(e.target.checked)} />}
              label="Incluir rango inactivo"
            />
          </Grid>
        </Grid>

        <Grid container spacing={1} mt={2}>
          <Grid item xs={12} sm={6}>
            <Button variant="contained" fullWidth onClick={submit} disabled={submitting} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
              {submitting ? "Programando envío..." : "Programar envío"}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                reset();
                close();
              }}
            >
              Cerrar
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}

export default PushSendModal;
